import React, { useState, useEffect, useRef } from "react";
import { useMeasure } from 'react-use';
import { Link } from "react-router-dom";
import Block2 from './Block2';
import MovingLocator from "./MovingLocator";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom';
import ViewListIcon from '@mui/icons-material/ViewList';
import InfoIcon from '@mui/icons-material/Info';
// import InsertLinkIcon from '@mui/icons-material/InsertLink';
// import LinkOffIcon from '@mui/icons-material/LinkOff';
import MoreVertIcon from '@mui/icons-material/MoreVert';

export default function Group(props) {
  const { groupId, groupOrder, title, blocks, timeCreated, link, height, onHeightChange, 
    onWidthChange, recalcOrder, onDelete, deleteBlock, updateGroup,
    moveGroup, moveBlock, isEditing, isProfile, isPrivate, isSkinViewer, addBlockToGroup, total,
    pageAuthor, currentUser, pageSkin, isSearch, pageCode, searchTerm, filter, createGroupLink, breakGroupLink } = props;
  const [blockStartingHeights, setBlockStartingHeights] = useState([[0],[0],[0],[0],[0],[0],[0],[0],[0],[0],[0],[0],[{SH: 0, CS: 0, CE: 12}],[0]]);
  const [blockHeights, setBlockHeights] = useState([20]);
  const [buttonHeights, setButtonHeights] = useState(26)
  const [movingLocator, setMovingLocator] = useState([false, 0, 0, 100])
  const [opacity, setOpacity] = useState(1)
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [linkMenuOpen, setLinkMenuOpen] = useState(false);
  const blocksRef = useRef(blocks);
  const trueGroupWidth = window.innerWidth <= 600? 88 : pageSkin?.groupWidth ;
  const groupWidth = Math.floor((window.innerWidth * (trueGroupWidth / 100)) / 12) * 12;
  const [titleRef, {height: titleHeight}] = useMeasure();
  const menuRef = useRef(null);

    // Function to initialize blockStartingHeights based on blocks
    useEffect(() => {
      const initializeBlockStartingHeights = () => {
        const initialStartingHeights = Array(13).fill().map(() => [0]); // Initialize empty columns
        var blockHeightRow = blocksRef.current.map((block, index) => ({
          SH: index * 20,
          CS: block.columnStart,
          CE: block.columnEnd,
        }));
        if (blocksRef.current.length === 0) {
          blockHeightRow = [{SH: 0, CS: 0, CE: 6}]
        }
    
        initialStartingHeights[12] = blockHeightRow; // Add block data to row 12
    
        let initialBlockHeights = [];
    
        // Update heights in columns corresponding to block's CS and CE
        blockHeightRow.forEach((block) => {
          for (let col = block.CS; col < block.CE; col++) {
            initialStartingHeights[col].push(block.SH); // Placeholder for future height calculations
          }
          initialBlockHeights.push(0);
        });
    
        setBlockHeights(initialBlockHeights);
        setBlockStartingHeights(initialStartingHeights);
        setIsInitialized(true); // Set initialized to true once setup is complete
      };
    
      if (!isInitialized) {
        initializeBlockStartingHeights();
      }
    }, [blocks, isInitialized, searchTerm, filter]);

    useEffect(() => {
      blocksRef.current = blocks;
    }, [blocks]);

    useEffect(() => {
      if (isInitialized) {
        setIsInitialized(false);
      } // eslint-disable-next-line
    }, [titleHeight]);

    useEffect(() => {
      function handleClickOutside(event) {
          if (menuRef.current && !menuRef.current.contains(event.target)) {
              setLinkMenuOpen(false);
          }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
          document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);    

  const updateBlockHeight = (blockOrder, newHeight) => {
        setBlockHeights(prevBlockHeights => {
          let updatedBlockHeights = [...prevBlockHeights];
          if (blocksRef.current.length > 0) {
            updatedBlockHeights[blockOrder] = newHeight;
            updatedBlockHeights[blocksRef.current.length] = 0;
          } else {
            updatedBlockHeights = [0];
          }

          let updatedBlockStartingHeights = [[0], [0], [0], [0], [0], [0], [0], [0], [0], [0], [0], [0], [{
            SH: 0, 
            CS: blocksRef.current[0] ? (blocksRef.current[0].columnStart || 0) : 0, 
            CE: blocksRef.current[0] ? (blocksRef.current[0].columnEnd || 6) : 6
          }], [0]];

          for (let k = updatedBlockStartingHeights[12][0].CS; k < updatedBlockStartingHeights[12][0].CE; k++) {
            updatedBlockStartingHeights[k].push(updatedBlockStartingHeights[12][0].SH + updatedBlockHeights[0] + 10);
          }

          for (let i = 1; i < blocksRef.current.length; i++) {
            updatedBlockStartingHeights[12].push({ SH: 0, CS: blocksRef.current[i].columnStart, CE: blocksRef.current[i].columnEnd });
          }

          for (let i = 1; i < blocksRef.current.length; i++) {
            const pSH = [];
            for (let l = updatedBlockStartingHeights[12][i].CS; l < updatedBlockStartingHeights[12][i].CE; l++) {
              pSH.push(updatedBlockStartingHeights[l][updatedBlockStartingHeights[l].length - 1]);
            }
            const nSH = pSH.reduce((a, b) => Math.max(a, b), -Infinity);
            updatedBlockStartingHeights[12][i].SH = nSH;
            for (let k = updatedBlockStartingHeights[12][i].CS; k < updatedBlockStartingHeights[12][i].CE; k++) {
              updatedBlockStartingHeights[k].push(updatedBlockStartingHeights[12][i].SH + updatedBlockHeights[i] + 10);
            }
          }

          let pHSH = [];
          for (let i = 0; i < 12; i++) {
            pHSH.push(updatedBlockStartingHeights[i][updatedBlockStartingHeights[i].length - 1]);
          }
          let hSH = pHSH.reduce((a, b) => Math.max(a, b), -Infinity);
          let calculatedGroupHeight = hSH + titleHeight + 10;

          updatedBlockStartingHeights[12].push({ SH: hSH, CS: 0, CE: 12 });
          for (let i = 1; i < updatedBlockStartingHeights[12].length; i++) {
            updatedBlockStartingHeights[13].push(updatedBlockStartingHeights[12][i].SH);
          }

          setTimeout(() => {
            onHeightChange(groupOrder, calculatedGroupHeight);
          }, 0)
          
          updatedBlockStartingHeights[12] = recalcOrder(groupId, groupOrder, updatedBlockStartingHeights[12]);
          if (updatedBlockStartingHeights[12][updatedBlockStartingHeights[12].length - 1] === true) {
            updatedBlockStartingHeights[12].pop()
          }

          setBlockStartingHeights(updatedBlockStartingHeights);

          setButtonHeights(calculatedGroupHeight - 4);
          return updatedBlockHeights;
        });
  };

  const updateBlockWidth = ((blockId, blockOrder, newColumnStart, newColumnEnd, newHeight) => {
    onWidthChange( groupOrder, blockId, blockOrder, newColumnStart, newColumnEnd)
        updateBlockHeight(blockOrder, newHeight)
  })

  function onDeleteBlock(blockId, blockOrder) {
    deleteBlock(groupId, groupOrder, blockId, blockOrder);
    setTimeout(() => {
      updateBlockHeight(blockOrder, 0)
    }, 0)
  }

  function onEditGroup() {
    if (isSkinViewer) return;
    updateGroup(groupId, groupOrder, "editGroup", null, null);
  }

  function onEditBlock(blockId, blockOrder) {
    updateGroup(groupId, groupOrder, "editBlock", blockId, blockOrder);
  }

  function handleDeleteClick() {
    onDelete(groupId, groupOrder);
  }

  function handleRearrangeClick(where) {
    moveGroup( groupOrder, where);
  }

  function handleBlockMove(blockId, blockOrder, desiredOrder, blockHeight, newColumnStart, newColumnEnd) {
    moveBlock(groupId, groupOrder, blockId, blockOrder, desiredOrder, newColumnStart, newColumnEnd)
      .then(() => {
        setBlockHeights(prevBlockHeights => {
          let updatedBlockHeights = [...prevBlockHeights];
          let movingBlockHeight = updatedBlockHeights[blockOrder];
          updatedBlockHeights = updatedBlockHeights.filter((_, index) => index !== blockOrder);
          updatedBlockHeights.splice(desiredOrder, 0, movingBlockHeight);
          return updatedBlockHeights;
        });
  
        updateBlockHeight(desiredOrder, blockHeight, newColumnStart, newColumnEnd);
      });
  }

  function createLocator (height, start, width) {
    setMovingLocator([true, height, start, width]);
  }
  function endLocator(){
    setMovingLocator([false, 0, 0, 100])
  }

  const groupCreateButtonStyle = {
    height:`${buttonHeights}px`,
    left:`${groupWidth -7}px`
  }

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function highlightText(text, phrase) {
    if (!phrase) return text;
    const regex = new RegExp(`(${escapeRegExp(phrase)})`, 'gi');
    return text.replace(regex, '<mark>$1</mark>');
  } 

  function handleCreateLink() {
    createGroupLink(groupId, groupOrder);
  }
  function handleBreakLink() {
    breakGroupLink(groupId, groupOrder);
  }
  function handleCopyLink() {
    navigator.clipboard.writeText(`willow-web.net/group/${link}`);
  }

  return (
    <div className="group-container" style={{width: groupWidth, 
    ...( isSearch  && !pageSkin.backgroundImage ? {backgroundColor: pageSkin?.pageBackgroundColor} : {})}} id={"group" + groupId}>
      {confirmingClose && !isSkinViewer && 
      <div id="close-confirm">
        <h2>are you sure you want to delete this?</h2>
        <div>
          <button className="block-type-option" onClick={() => {
            setConfirmingClose(false)
            handleDeleteClick()
            }}>
            OK
          </button>
          <button className="block-type-option" onClick={() => setConfirmingClose(false)}>Cancel</button>
        </div>
      </div>}
      {isSearch && pageSkin?.backgroundImage && <img className="search-group-background-image" src={pageSkin.backgroundImage} alt="background"/>}
      <div className="group-content" style={{width: groupWidth}}>
        <div
          className={isSkinViewer ? "block group skin-viewer-group" : "block group"}
          style={{height: height, 
            width: groupWidth,
            ...((isEditing && !isSkinViewer) || pageSkin?.groupBorders === 1 ? { border: `1px solid ${pageSkin?.borderColor}` } : {}),
            ...(isSearch && {backgroundColor: pageSkin?.groupBackgroundColor, borderRadius: pageSkin?.curvedBorders === 1? "10px" : "0"})
          }}
        >
          <div className="block-inner">
            <div ref={titleRef}>
              {(isEditing || isSearch || title?.length > 0)&& <div className="group-title" style={(isEditing || isSearch || title?.length > 0) ? {borderBottom: `1px solid ${pageSkin?.titleBorderColor}`} : {borderBottom: `none`}}>
                {!isSearch || isPrivate === 1? <h4>{title}</h4> : 
                <Link to={isProfile? `/profile/${pageAuthor}`:`/${pageAuthor}/page/${pageCode}`}>
                  <h4 dangerouslySetInnerHTML={{__html: highlightText(title, searchTerm),}}
                    style={{fontSize: `${pageSkin?.textSize + 4}px`,
                      fontFamily: `${pageSkin?.textFont}, Times New Roman`,
                      color: pageSkin?.textColor}}
                  />
                </Link >}
                {isEditing && !isSkinViewer && <div className="group-edit-buttons">
                  <button onClick={onEditGroup}><EditIcon /></button>
                  <button onClick={() => {setConfirmingClose(true)}}><DeleteIcon /></button>
                  <button id="group-info-icon"><InfoIcon />
                    <div className="time-created">
                      {timeCreated? <>this group was created on {timeCreated}</> : isSkinViewer ? <></> : <>this group was created before time created was measured.</>}
                    </div>
                  </button>
                  <button onClick={() => {setLinkMenuOpen((prev) => {return !prev})}} ref={menuRef}><MoreVertIcon />
                    {linkMenuOpen && !isSkinViewer && <div className="link-menu" style={{bottom: `${buttonHeights + 10}px`}} >
                      {link? <div onClick={handleBreakLink}>break link</div> : <div onClick={handleCreateLink}>create link</div>}
                      {link && <div onClick={handleCopyLink}>copy link</div>}
                      </div>}
                  </button>
                </div>}      
              </div>}
            </div>
            <div className="block-content">
              <MovingLocator what={movingLocator}/>
              {isInitialized && blocks.length > 0 && blocks.map((blockItem, index) => {
                return (<Block2
                parent={groupId}
                key={blockItem.id}
                id={blockItem.id}
                order={blockItem.order}
                type={blockItem.type}
                retract={blockItem.retract} 
                title={blockItem.title} 
                content={blockItem.content} 
                maxWidth={groupWidth - 12}
                startingHeight={blockStartingHeights[12][blockItem.order].SH}
                height={blockHeights[index]}
                columnStart={blockStartingHeights[12][blockItem.order].CS}
                columnEnd={blockStartingHeights[12][blockItem.order].CE}
                onHeightChange={updateBlockHeight}
                onWidthChange={updateBlockWidth}
                onRearrange={handleBlockMove}
                createLocator={createLocator}
                endLocator={endLocator}
                onDelete={onDeleteBlock}
                onEdit={onEditBlock}
                overallStartingHeights={blockStartingHeights}
                opacity={opacity}
                setOpacity={setOpacity}
                isEditing={isEditing}
                total={total}
                pageAuthor={pageAuthor}
                currentUser={currentUser}
                pageSkin={pageSkin}
                searchTerm={searchTerm}
                isSearch={isSearch}
                groupHeight={height}
                groupTitleHeight={titleHeight}
                isSkinViewer={isSkinViewer}
              />)})}
            </div>
          </div>
        </div>
        {isEditing && !isSkinViewer && <div 
          className="group-side-options" 
          style={groupCreateButtonStyle}
        >
          <div className="group-movement-options">
            <div className="button" onClick={() => handleRearrangeClick(999999)}><VerticalAlignTopIcon /></div>
            <div className="button" onClick={() => handleRearrangeClick(5)}>+5</div>
            <div className="button" onClick={() => handleRearrangeClick(1)}>+1</div>
            <div className="button" onClick={() => handleRearrangeClick(-1)}>-1</div>
            <div className="button" onClick={() => handleRearrangeClick(-5)}>-5</div>
            <div className="button" onClick={() => handleRearrangeClick(-999999)}><VerticalAlignBottomIcon /></div>
          </div>
          <div 
            className="button block-create-button" 
            onClick={() => {
              if (blocks.length !== 0) {
                var ids = [];
                for (let i = 0; i < blocks.length; i++) {
                  ids.push(blocks[i].id);
                }
                const newId = ids.reduce((a, b) => Math.max(a,b), -Infinity) + 1;
                updateGroup(groupId, groupOrder, "addBlock", newId, blocks.length)
              } else {
                updateGroup(groupId, groupOrder, "addBlock", 0, 0)
              }
            }}
          ><AddIcon /></div>
          {isProfile && <div 
            className="button block-create-button" 
            onClick={() => {
              if (blocks.length !== 0) {
                var ids = [];
                for (let i = 0; i < blocks.length; i++) {
                  ids.push(blocks[i].id);
                }
                const newId = ids.reduce((a, b) => Math.max(a,b), -Infinity) + 1;
                addBlockToGroup(groupId, groupOrder, newId, blocks.length, "folders", "unretractable", "Collections",null);
              } else {
                addBlockToGroup(groupId, groupOrder, 0, 0, "folders", "unretractable", "Collections", null);
              }
            }}
          ><ViewListIcon /></div>}
        </div>}
      </div>
    </div>
  );
}